import { useEffect, useRef, useState } from 'react';
import axios from 'src/axios';
import { getURL } from 'src/utils/config';
import useHLSVideoPlayer from 'src/hooks/useHLSVideoPlayer';
import {
  Box, Button, Divider, FormControl, IconButton, MenuItem,
  Popover, Select, Slider, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import Replay10RoundedIcon from '@mui/icons-material/Replay10Rounded';
import Forward10RoundedIcon from '@mui/icons-material/Forward10Rounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeDownRoundedIcon from '@mui/icons-material/VolumeDownRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';

const SPEEDS = [0.5, 1, 1.5, 2, 3, 4, 5, 6, 8, 10];
const EXPIRY_OPTIONS = ['1 hour', '6 hours', '1 day', '3 days', '7 days', '15 days'];
const EXPIRY_HOURS = { '1 hour': 1, '6 hours': 6, '1 day': 24, '3 days': 72, '7 days': 168, '15 days': 360 };

const BORDER_COLOR = 'rgba(255,255,255,0.15)';

const btnSx = {
  width: 30,
  height: 30,
  color: 'text.secondary',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
};

function fmt(s) {
  const t = Math.floor(s || 0);
  const m = Math.floor(t / 60);
  const sec = t % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function AuditVideoPlayer({
  sourceUrl,
  cameraLabel = '',
  cameraId = null,
  eventNo = null,
  fromTime = null,
  toTime = null,
  onClose,
  infoContent,
  disableShare = false,
}) {
  const videoRef = useRef(null);
  const isDraggingRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const seekThrottleRef = useRef(0);

  // Progress bar state — tracked separately from the hook
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState(0);

  // Volume/mute — not managed by hook
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // UI state
  const [speedAnchor, setSpeedAnchor] = useState(null);
  const [infoOpen, setInfoOpen] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  // Share panel state
  const [shareExpiry, setShareExpiry] = useState('1 day');
  const [shareReceiverName, setShareReceiverName] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [shareComments, setShareComments] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareError, setShareError] = useState('');

  const {
    playerState,
    togglePlay,
    handleVideoSpeed,
    onVideoLoaded,
    onVideoError,
    handleOnTimeUpdate,
  } = useHLSVideoPlayer(!!sourceUrl, videoRef, null, sourceUrl, 0);

  // Wire loadedmetadata / timeupdate / error to the hook + local progress bar
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const onLoaded = () => {
      setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      setCurrentTime(0);
      onVideoLoaded();
    };
    const onTimeUpdate = () => {
      handleOnTimeUpdate();
      if (!isDraggingRef.current) setCurrentTime(el.currentTime);
    };
    const onError = (e) => onVideoError(e);

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('error', onError);

    return () => {
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('error', onError);
    };
  }, []); // attach once — hook handles re-loading on sourceUrl change

  // Sync volume / mute to video element
  useEffect(() => {
    const el = videoRef.current;
    if (el) { el.muted = isMuted; if (!isMuted) el.volume = volume; }
  }, [isMuted, volume]);

  const seekTo = (t) => {
    const el = videoRef.current;
    if (!el) return;
    const v = Math.max(0, Math.min(duration || 0, t));
    el.currentTime = v;
    setCurrentTime(v);
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setIsMuted(el.muted);
  };

  const enterFullScreen = async () => {
    const el = videoRef.current;
    if (!el) return;
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (el.requestFullscreen) await el.requestFullscreen();
  };

  const changeSpeed = (s) => {
    handleVideoSpeed({ target: { value: String(s) } });
    setSpeedAnchor(null);
  };

  const openSharePanel = () => {
    setShareExpiry('1 day');
    setShareReceiverName('');
    setShareEmail('');
    setShareComments('');
    setShareError('');
    setInfoOpen(false);
    setShareOpen(true);
  };

  const handleSendLink = async () => {
    const params = new URLSearchParams();
    params.append('access_link', `${window.location.origin}/shared-footage`);
    params.append('expires_at', EXPIRY_HOURS[shareExpiry] ?? 24);
    params.append('camera_id', cameraId ?? '');
    params.append('from_time', '');
    params.append('to_time', '');
    params.append('note', shareComments);
    params.append('email', shareEmail);
    params.append('reciever_name', shareReceiverName);
    params.append('duration', Math.round(duration) || 60);
    params.append('event_no', eventNo ?? '');
    params.append('link_type', 'escalation');

    setShareSending(true);
    setShareError('');
    try {
      await axios.post(getURL('api-access-links/add'), params);
      setShareOpen(false);
    } catch (err) {
      setShareError(err?.response?.data?.detail || 'Failed to send link. Please try again.');
    } finally {
      setShareSending(false);
    }
  };

  const volIcon = isMuted || volume === 0
    ? <VolumeOffRoundedIcon />
    : volume < 0.5 ? <VolumeDownRoundedIcon /> : <VolumeUpRoundedIcon />;

  const panelSx = {
    width: { xs: '100%', sm: 310 },
    height: { xs: 'auto', sm: '100%' },
    maxHeight: { xs: '55%', sm: 'none' },
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderLeft: { xs: 'none', sm: `1px solid ${BORDER_COLOR}` },
    borderTop: { xs: `1px solid ${BORDER_COLOR}`, sm: 'none' },
    bgcolor: 'background.paper',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, bgcolor: 'background.default', overflow: 'hidden' }}>
      {/* Player column */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'grid', gridTemplateRows: 'auto minmax(0,1fr) auto', overflow: 'hidden' }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 0.75 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary' }}>{cameraLabel}</Typography>
          </Stack>
          {onClose && (
            <IconButton size="small" sx={btnSx} onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          )}
        </Stack>

        {/* Video */}
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', aspectRatio: '16/9', height: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            <Box
              component="video"
              ref={videoRef}
              muted
              playsInline
              preload="metadata"
              // clicking the picture toggles play/pause, same as the control-bar button
              onClick={togglePlay}
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'pointer' }}
            />
            {playerState.playStatus === 'Error' && (
              <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', px: 1.5, py: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.72)', color: '#fff', border: '1px solid rgba(255,255,255,0.18)', fontSize: 12, whiteSpace: 'nowrap' }}>
                Unable to play this video
              </Box>
            )}
          </Box>
        </Box>

        {/* Controls */}
        <Box sx={{ px: 1.5, pt: 0.75, pb: 1, borderTop: `1px solid ${BORDER_COLOR}` }}>
          {/* Progress bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', minWidth: 36, flexShrink: 0 }}>
              {fmt(currentTime)}
            </Typography>
            <Slider
              min={0}
              max={duration || 1}
              step={0.5}
              value={isDragging ? dragValue : currentTime}
              onMouseDown={() => {
                isDraggingRef.current = true;
                setIsDragging(true);
                setDragValue(currentTime);
                wasPlayingRef.current = !videoRef.current?.paused;
                videoRef.current?.pause();
              }}
              onChange={(_, v) => {
                const n = Number(v);
                setDragValue(n);
                const now = Date.now();
                if (now - seekThrottleRef.current >= 300) {
                  seekThrottleRef.current = now;
                  seekTo(n);
                }
              }}
              onChangeCommitted={(_, v) => {
                const n = Number(v);
                seekTo(n);
                isDraggingRef.current = false;
                setIsDragging(false);
                if (wasPlayingRef.current) videoRef.current?.play().catch(() => {});
              }}
              sx={{
                flex: 1, height: 4, color: 'primary.main',
                '& .MuiSlider-thumb': { width: 12, height: 12, '&:hover': { boxShadow: '0 0 0 6px rgba(25,118,210,0.16)' } },
                '& .MuiSlider-rail': { opacity: 0.3 },
              }}
            />
            <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', minWidth: 36, flexShrink: 0, textAlign: 'right' }}>
              {fmt(duration)}
            </Typography>
          </Box>

          {/* Button row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <IconButton sx={btnSx} onClick={togglePlay}>
                {playerState.isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </IconButton>
              <IconButton sx={btnSx} onClick={() => seekTo(currentTime - 10)}><Replay10RoundedIcon /></IconButton>
              <IconButton sx={btnSx} onClick={() => seekTo(currentTime + 10)}><Forward10RoundedIcon /></IconButton>
              <IconButton sx={{ ...btnSx, position: 'relative' }} onClick={(e) => setSpeedAnchor(e.currentTarget)}>
                <SpeedRoundedIcon />
                <Typography sx={{ fontSize: 7, position: 'absolute', bottom: 1, right: 2, color: 'primary.main', lineHeight: 1, fontWeight: 700 }}>
                  {playerState.speed}x
                </Typography>
              </IconButton>
              <Popover
                open={Boolean(speedAnchor)}
                anchorEl={speedAnchor}
                onClose={() => setSpeedAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{ paper: { sx: { p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' } } }}
              >
                <Typography sx={{ fontSize: 10, color: 'text.disabled', mb: 0.75, textAlign: 'center', letterSpacing: '0.06em' }}>PLAYBACK SPEED</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5 }}>
                  {SPEEDS.map((s) => (
                    <Box
                      key={s}
                      onClick={() => changeSpeed(s)}
                      sx={{
                        px: 0.75, py: 0.4, borderRadius: 1, fontSize: 11,
                        textAlign: 'center', cursor: 'pointer', userSelect: 'none',
                        fontWeight: playerState.speed === s ? 700 : 400,
                        color: playerState.speed === s ? 'primary.contrastText' : 'text.secondary',
                        bgcolor: playerState.speed === s ? 'primary.main' : 'transparent',
                        border: '1px solid', borderColor: playerState.speed === s ? 'primary.main' : 'divider',
                        '&:hover': { borderColor: 'primary.main', color: playerState.speed === s ? 'primary.contrastText' : 'primary.main' },
                      }}
                    >
                      {s}x
                    </Box>
                  ))}
                </Box>
              </Popover>
              <IconButton sx={btnSx} onClick={toggleMute}>{volIcon}</IconButton>
              <Slider
                min={0} max={1} step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(_, v) => {
                  const n = Number(v);
                  setVolume(n);
                  const el = videoRef.current;
                  if (el) el.volume = n;
                  if (n > 0 && isMuted) { setIsMuted(false); if (el) el.muted = false; }
                }}
                sx={{
                  width: 60,
                  color: 'text.secondary',
                  '& .MuiSlider-rail': { opacity: 0.35 },
                  '& .MuiSlider-thumb': { width: 11, height: 11 },
                }}
              />
            </Stack>

            <Stack direction="row" alignItems="center">
              <Tooltip title="Event info">
                <IconButton sx={btnSx} onClick={() => { setShareOpen(false); setInfoOpen((v) => !v); }}>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
              {!disableShare && (
                <Tooltip title="Share clip">
                  <IconButton sx={btnSx} onClick={openSharePanel}>
                    <IosShareRoundedIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton sx={btnSx} onClick={enterFullScreen}>
                <FullscreenRoundedIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Info panel */}
      {infoOpen && (
        <Box sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: `1px solid ${BORDER_COLOR}`, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Event Info</Typography>
            <IconButton size="small" sx={btnSx} onClick={() => setInfoOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            {infoContent}
          </Box>
        </Box>
      )}

      {/* Share panel */}
      {!disableShare && shareOpen && (
        <Box sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: `1px solid ${BORDER_COLOR}`, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Share Footage</Typography>
            <IconButton size="small" sx={btnSx} onClick={() => setShareOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: 13, color: 'text.primary' }}>Link Expires</Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={shareExpiry} onChange={(e) => setShareExpiry(e.target.value)} sx={{ fontSize: 12 }}>
                  {EXPIRY_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField fullWidth size="small" label="Recipient Name" placeholder="Enter recipient name…"
              value={shareReceiverName} onChange={(e) => setShareReceiverName(e.target.value)} sx={{ mb: 2 }} />

            <TextField fullWidth size="small" label="Email" placeholder="recipient@example.com" type="email"
              value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} sx={{ mb: 2 }} />

            <TextField fullWidth size="small" label="Comments" placeholder="Add a note…" multiline minRows={3}
              value={shareComments} onChange={(e) => setShareComments(e.target.value)} sx={{ mb: 2 }} />

            <Divider sx={{ mb: 2 }} />

            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
              Shared video and features can be viewed by anyone with access to the link URL
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER_COLOR}`, flexShrink: 0 }}>
            {shareError && (
              <Typography sx={{ fontSize: 11, color: 'error.main', mb: 1, lineHeight: 1.4 }}>{shareError}</Typography>
            )}
            <Button fullWidth variant="contained" disabled={shareSending} onClick={handleSendLink} sx={{ borderRadius: 1.5 }}>
              {shareSending ? 'Sending…' : 'Send Link'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
